import { api } from "../api";

/*
 * Centraliza todas as chamadas do módulo de projetos.
 *
 * Os métodos antigos foram mantidos:
 * - create
 * - list
 * - me
 * - get
 * - assign
 * - patchStatus
 *
 * Os novos métodos suportam:
 * - participantes colaborativos;
 * - responsável principal;
 * - controle de fila;
 * - sessões de trabalho;
 * - troca de atividade;
 * - incidentes urgentes;
 * - bloqueios;
 * - tarefas;
 * - comentários;
 * - anexos;
 * - auditoria.
 */

const projetoUrl = (idProjeto) => `/projetos/${idProjeto}`;

const participanteUrl = (idProjeto, devId) =>
  `${projetoUrl(idProjeto)}/participantes/${devId}`;

const tarefaUrl = (idProjeto, idTarefa = null) =>
  idTarefa
    ? `${projetoUrl(idProjeto)}/tarefas/${idTarefa}`
    : `${projetoUrl(idProjeto)}/tarefas`;

const bloqueioUrl = (idProjeto, idBloqueio = null) =>
  idBloqueio
    ? `${projetoUrl(idProjeto)}/bloqueios/${idBloqueio}`
    : `${projetoUrl(idProjeto)}/bloqueios`;

function normalizarPayloadAtribuicao(devIdOuPayload, opcoes = {}) {
  if (
    devIdOuPayload &&
    typeof devIdOuPayload === "object" &&
    !Array.isArray(devIdOuPayload)
  ) {
    return devIdOuPayload;
  }

  return {
    devId: Number(devIdOuPayload),
    ...opcoes,
  };
}

export const ProjetosApi = {
  /* =========================================================
     PROJETOS
     ========================================================= */

  create: (payload) => api.post("/projetos", payload),

  list: (params = {}) => api.get("/projetos", { params }),

  me: (devId, params = {}) =>
    api.get("/projetos/me", {
      params: {
        devId,
        ...params,
      },
    }),

  get: (idProjeto) => api.get(projetoUrl(idProjeto)),

  patch: (idProjeto, payload) =>
    api.patch(projetoUrl(idProjeto), payload),

  update: (idProjeto, payload) =>
    api.put(projetoUrl(idProjeto), payload),

  patchStatus: (idProjeto, payload) =>
    api.patch(`${projetoUrl(idProjeto)}/status`, payload),

  encaminharSuporte: (idProjeto, payload) =>
    api.post(`${projetoUrl(idProjeto)}/encaminhar-suporte`, payload),

  /* =========================================================
     ATRIBUIÇÃO LEGADA E FOCO
     ========================================================= */

  /*
   * Compatível com:
   * ProjetosApi.assign(idProjeto, devId)
   *
   * E também com:
   * ProjetosApi.assign(idProjeto, {
   *   devId,
   *   exclusivo: false,
   *   papel: "COLABORADOR"
   * })
   */
  assign: (idProjeto, devIdOuPayload, opcoes = {}) =>
    api.post(
      `${projetoUrl(idProjeto)}/atribuir`,
      normalizarPayloadAtribuicao(devIdOuPayload, opcoes)
    ),

  definirFoco: (idProjeto, payload) =>
    api.post(`${projetoUrl(idProjeto)}/foco`, payload),

  /* =========================================================
     FILA DO DESENVOLVEDOR
     ========================================================= */

  filaDoDev: (devId, params = {}) =>
    api.get(`/projetos/devs/${devId}/fila`, { params }),

  atualizarFilaDoDev: (devId, ids) =>
    api.patch(`/projetos/devs/${devId}/fila`, {
      ids: Array.isArray(ids) ? ids : [],
    }),

  /* =========================================================
     PARTICIPANTES COLABORATIVOS
     ========================================================= */

  listarParticipantes: (idProjeto, somenteAtivos = false) =>
    api.get(`${projetoUrl(idProjeto)}/participantes`, {
      params: { somenteAtivos },
    }),

  adicionarParticipante: (idProjeto, payload) =>
    api.post(`${projetoUrl(idProjeto)}/participantes`, payload),

  atualizarParticipante: (idProjeto, devId, payload) =>
    api.patch(participanteUrl(idProjeto, devId), payload),

  removerParticipante: (
    idProjeto,
    devId,
    payload = {}
  ) =>
    api.delete(participanteUrl(idProjeto, devId), {
      data: payload,
    }),

  definirResponsavelPrincipal: (
    idProjeto,
    devId,
    payload = {}
  ) =>
    api.patch(participanteUrl(idProjeto, devId), {
      ...payload,
      responsavelPrincipal: true,
      papel: "RESPONSAVEL_PRINCIPAL",
      ativo: true,
    }),

  /* =========================================================
     SESSÕES DE TRABALHO
     ========================================================= */

  obterAtividadeAtual: (devId) =>
    api.get("/projetos/me/atividade-atual", {
      params: { devId },
    }),

  iniciarTrabalho: (payload) =>
    api.post("/projetos/trabalho/iniciar", payload),

  pararTrabalho: (payload) =>
    api.post("/projetos/trabalho/parar", payload),

  trocarAtividade: (payload) =>
    api.post("/projetos/trabalho/trocar", payload),

  listarSessoesTrabalho: (params = {}) =>
    api.get("/projetos/trabalho/sessoes", { params }),

  /* =========================================================
     INCIDENTES URGENTES
     ========================================================= */

  criarIncidenteRapido: (payload) =>
    api.post("/projetos/incidentes/rapido", payload),

  /* =========================================================
     BLOQUEIOS
     ========================================================= */

  listarBloqueios: (idProjeto, somenteAbertos = false) =>
    api.get(bloqueioUrl(idProjeto), {
      params: { somenteAbertos },
    }),

  criarBloqueio: (idProjeto, payload) =>
    api.post(bloqueioUrl(idProjeto), payload),

  resolverBloqueio: (idProjeto, idBloqueio, payload) =>
    api.post(
      `${bloqueioUrl(idProjeto, idBloqueio)}/resolver`,
      payload
    ),

  /* =========================================================
     TAREFAS
     ========================================================= */

  listarTarefas: (idProjeto) =>
    api.get(tarefaUrl(idProjeto)),

  criarTarefa: (idProjeto, payload) =>
    api.post(tarefaUrl(idProjeto), payload),

  atualizarTarefa: (idProjeto, idTarefa, payload) =>
    api.patch(tarefaUrl(idProjeto, idTarefa), payload),

  /* =========================================================
     AUDITORIA
     ========================================================= */

  listarUpdates: (idProjeto, params = {}) =>
    api.get(`${projetoUrl(idProjeto)}/updates`, { params }),

  /* =========================================================
     COMENTÁRIOS
     ========================================================= */

  listarComentarios: (idProjeto) =>
    api.get(`${projetoUrl(idProjeto)}/comentarios`),

  incluirComentario: (idProjeto, payload) =>
    api.post(`${projetoUrl(idProjeto)}/comentarios`, payload),

  removerComentario: (idProjeto, idComentario) =>
    api.delete(
      `${projetoUrl(idProjeto)}/comentarios/${idComentario}`
    ),

  /* =========================================================
     ANEXOS
     ========================================================= */

  listarAnexos: (idProjeto) =>
    api.get(`${projetoUrl(idProjeto)}/anexos`),

  enviarAnexo: (
    idProjeto,
    arquivo,
    onUploadProgress = undefined
  ) => {
    const formData = new FormData();
    formData.append("file", arquivo);

    return api.post(
      `${projetoUrl(idProjeto)}/anexos`,
      formData,
      {
        onUploadProgress,
      }
    );
  },

  removerAnexo: (idProjeto, idAnexo) =>
    api.delete(
      `${projetoUrl(idProjeto)}/anexos/${idAnexo}`
    ),

  urlAnexoInline: (idProjeto, idAnexo) =>
    `${projetoUrl(idProjeto)}/anexos/${idAnexo}/inline`,

  urlDownloadAnexo: (idProjeto, idAnexo) =>
    `${projetoUrl(idProjeto)}/anexos/${idAnexo}/download`,

  /* =========================================================
     NOVIDADES
     ========================================================= */

  novidades: (params = {}) =>
    api.get("/projetos/novidades", { params }),
};

export default ProjetosApi;
